import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import styles from './IntercompanyBillViewer.module.css';
import {
  INTERCOMPANY_DETAILED_INVOICE,
  INTERCOMPANY_INVOICE_LIST_BY_DATE_AND_TIME,
  INTERCOMPANY_INVOICE_LIST_BY_DATE,
} from '../constant/apiConstants';
import PrintBillModal from '../print/PrintBillModal.js';

// True when an axios error is the result of an aborted/cancelled request.
const isCanceledError = (err) =>
  axios.isCancel?.(err) ||
  err?.code === 'ERR_CANCELED' ||
  err?.name === 'CanceledError' ||
  err?.name === 'AbortError';

const IntercompanyBillViewer = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [bills, setBills] = useState([]);
  const [error, setError] = useState('');
  const [selectedBill, setSelectedBill] = useState(null);
  const [activeFilter, setActiveFilter] = useState('Today');
  const [sortOrder, setSortOrder] = useState('Recent');
  const [quarters, setQuarter] = useState(null);
  const [selectedQuarter, setSelectedQuarter] = useState('');

  // Loading state for any data-fetching API call
  const [isLoading, setIsLoading] = useState(false);
  // Holds the AbortController for the in-flight list fetch so it can be cancelled.
  const fetchAbortRef = useRef(null);
  // Tracks which bill's PDF/thermal print is being generated
  const [printingBillNo, setPrintingBillNo] = useState(null);

  // Time range filter
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [startTimeHour, setStartTimeHour] = useState('');
  const [startTimeMinute, setStartTimeMinute] = useState('00');
  const [endTimeHour, setEndTimeHour] = useState('');
  const [endTimeMinute, setEndTimeMinute] = useState('00');
  const [startTimePeriod, setStartTimePeriod] = useState('AM');
  const [endTimePeriod, setEndTimePeriod] = useState('PM');
  const [isTimeFilterActive, setIsTimeFilterActive] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [billInfo, setBillInfo] = useState(null);

  // Local search (filters the loaded bills client-side)
  const [searchQuery, setSearchQuery] = useState('');

  // Custom date modal
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const convertTo24Hour = (hour, minute, period) => {
    if (!hour || !minute) return null;
    let hour24 = parseInt(hour);
    if (period === 'PM' && hour24 !== 12) hour24 += 12;
    else if (period === 'AM' && hour24 === 12) hour24 = 0;
    return `${hour24.toString().padStart(2, '0')}:${minute}`;
  };

  // Begin a new cancellable fetch: abort any previous one and return a fresh controller.
  const beginFetch = () => {
    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    setIsLoading(true);
    return controller;
  };

  // Clear loading only if the just-finished request is still the active one.
  const endFetch = (controller) => {
    if (fetchAbortRef.current === controller) {
      fetchAbortRef.current = null;
      setIsLoading(false);
    }
  };

  // User-triggered cancel: abort the in-flight request and discard it.
  const cancelFetch = () => {
    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
      fetchAbortRef.current = null;
    }
    setIsLoading(false);
  };

  const fetchBills = async () => {
    if (activeFilter === 'Custom Date') {
      if (!startDate || !endDate) return;
    }
    const controller = beginFetch();
    try {
      const user = JSON.parse(sessionStorage.getItem('user'));
      const storeId = user?.storeId;
      const response = await axios.get(`${INTERCOMPANY_INVOICE_LIST_BY_DATE}`, {
        params: { startDate, endDate, storeId },
        signal: controller.signal,
      });
      setBills(response.data);
      setError('');
    } catch (error) {
      if (isCanceledError(error)) return; // cancelled — discard silently
      if (error.response && error.response.status === 404) {
        setError('No bills found for the selected date range.');
      } else {
        setError('An error occurred while fetching bills.');
      }
    } finally {
      endFetch(controller);
    }
  };

  const fetchBillsWithTimeRange = async () => {
    if (!startDate || !endDate || !startTimeHour) {
      setError('Please select date range and start time');
      return;
    }
    const controller = beginFetch();
    try {
      const user = JSON.parse(sessionStorage.getItem('user'));
      const storeId = user?.storeId;
      const startTime24 = convertTo24Hour(startTimeHour, startTimeMinute, startTimePeriod);
      const endTime24 = endTimeHour ? convertTo24Hour(endTimeHour, endTimeMinute, endTimePeriod) : null;
      const response = await axios.get(`${INTERCOMPANY_INVOICE_LIST_BY_DATE_AND_TIME}`, {
        params: { startDate, endDate, startTime: startTime24, endTime: endTime24, storeId },
        signal: controller.signal,
      });
      setBills(response.data);
      setError('');
      setIsTimeFilterActive(true);
    } catch (error) {
      if (isCanceledError(error)) return; // cancelled — discard silently
      if (error.response && error.response.status === 404) {
        setError('No bills found for the selected date and time range.');
      } else {
        setError('An error occurred while fetching bills.');
      }
    } finally {
      endFetch(controller);
    }
  };

  const handleQuarterChange = (quarterKey) => {
    if (!quarters || !quarterKey) {
      setSelectedQuarter('');
      return;
    }
    const selected = quarters[quarterKey];
    if (selected) {
      setStartDate(selected.start);
      setEndDate(selected.end);
      setActiveFilter('Quarter');
      setSelectedQuarter(quarterKey);
    }
  };

  // Build current + previous FY quarters, each tagged with its FY group label.
  const getIndianFYQuarters = () => {
    const today = new Date();
    const currentFyStart = today.getMonth() < 3 ? today.getFullYear() - 1 : today.getFullYear();

    const makeQuarters = (startYear, group) => ({
      [`${group}-Q1`]: { label: `Q1 · Apr–Jun ${startYear}`, start: `${startYear}-04-01`, end: `${startYear}-06-30`, group },
      [`${group}-Q2`]: { label: `Q2 · Jul–Sep ${startYear}`, start: `${startYear}-07-01`, end: `${startYear}-09-30`, group },
      [`${group}-Q3`]: { label: `Q3 · Oct–Dec ${startYear}`, start: `${startYear}-10-01`, end: `${startYear}-12-31`, group },
      [`${group}-Q4`]: { label: `Q4 · Jan–Mar ${startYear + 1}`, start: `${startYear + 1}-01-01`, end: `${startYear + 1}-03-31`, group },
    });

    const fyLabel = (start) => `FY ${start}-${String(start + 1).slice(-2)}`;

    return {
      ...makeQuarters(currentFyStart, fyLabel(currentFyStart)),
      ...makeQuarters(currentFyStart - 1, fyLabel(currentFyStart - 1)),
    };
  };

  // Group quarters by their FY label so we can render <optgroup> separators.
  const quarterGroups = quarters
    ? Object.entries(quarters).reduce((acc, [key, q]) => {
        (acc[q.group] = acc[q.group] || []).push([key, q]);
        return acc;
      }, {})
    : {};

  useEffect(() => {
    setBills([]);
    if (startDate && endDate && !isTimeFilterActive) {
      fetchBills();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  useEffect(() => {
    setQuarter(getIndianFYQuarters());
    handleDateFilter('Today');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Abort any pending list fetch if the component unmounts.
  useEffect(() => {
    return () => {
      if (fetchAbortRef.current) {
        fetchAbortRef.current.abort();
        fetchAbortRef.current = null;
      }
    };
  }, []);

  const handleDateFilter = async (filter) => {
    const today = new Date();
    let calculatedStartDate = '';
    let calculatedEndDate = format(today, 'yyyy-MM-dd');
    setSelectedQuarter('');
    setIsTimeFilterActive(false);

    switch (filter) {
      case 'Today':
        calculatedStartDate = calculatedEndDate;
        break;
      case 'This Week': {
        const startOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
        calculatedStartDate = format(startOfWeek, 'yyyy-MM-dd');
        break;
      }
      case 'This Month':
        calculatedStartDate = format(new Date(today.getFullYear(), today.getMonth(), 1), 'yyyy-MM-dd');
        calculatedEndDate = format(new Date(today.getFullYear(), today.getMonth() + 1, 0), 'yyyy-MM-dd');
        break;
      case 'Previous Month': {
        const prevMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const prevMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        calculatedStartDate = format(prevMonthStart, 'yyyy-MM-dd');
        calculatedEndDate = format(prevMonthEnd, 'yyyy-MM-dd');
        break;
      }
      case 'This FY': {
        const currentFYStartMonth = today.getMonth() >= 3 ? 3 : -9;
        calculatedStartDate = format(new Date(today.getFullYear(), currentFYStartMonth, 1), 'yyyy-MM-dd');
        const endYear = currentFYStartMonth === -9 ? today.getFullYear() : today.getFullYear() + 1;
        calculatedEndDate = format(new Date(endYear, 2, 31), 'yyyy-MM-dd');
        break;
      }
      default:
        return;
    }

    setStartDate(calculatedStartDate);
    setEndDate(calculatedEndDate);
    setActiveFilter(filter);
  };

  // Custom date is a modal with a Find button.
  const openCustomDateModal = () => {
    setCustomStart(startDate || '');
    setCustomEnd(endDate || '');
    setShowCustomDateModal(true);
  };

  const applyCustomDate = () => {
    if (!customStart || !customEnd) {
      alert('Please select both start and end dates.');
      return;
    }
    if (customStart > customEnd) {
      alert('Start date cannot be after end date.');
      return;
    }
    setIsTimeFilterActive(false);
    setSelectedQuarter('');
    setActiveFilter('Custom Date');
    setStartDate(customStart);
    setEndDate(customEnd);
    setShowCustomDateModal(false);
  };

  const handleSortOrderChange = (order) => setSortOrder(order);
  const handleViewDetails = (bill) => setSelectedBill(bill);
  const closePopup = () => setSelectedBill(null);

  const handlePrintBill = async (billNo) => {
    setPrintingBillNo(billNo);
    try {
      const user = JSON.parse(sessionStorage.getItem('user'));
      const storeId = user?.storeId;
      const storeConfig = JSON.parse(sessionStorage.getItem('storeConfig') || '{}');
      const printType = (storeConfig.printType || 'PDF').trim().toUpperCase();

      if (printType === 'THERMAL') {
        const response = await axios.get(`${INTERCOMPANY_DETAILED_INVOICE}`, {
          params: { billNo, storeId, printType },
        });
        setBillInfo(response.data);
        setShowPrintModal(true);
      } else {
        const response = await axios.get(`${INTERCOMPANY_DETAILED_INVOICE}`, {
          params: { billNo, storeId, printType },
          responseType: 'arraybuffer',
        });
        const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
        const pdfUrl = URL.createObjectURL(pdfBlob);

        const printIframe = document.createElement('iframe');
        printIframe.style.position = 'absolute';
        printIframe.style.width = '0';
        printIframe.style.height = '0';
        printIframe.style.border = 'none';
        printIframe.style.visibility = 'hidden';
        document.body.appendChild(printIframe);

        printIframe.onload = function () {
          try {
            setTimeout(() => {
              printIframe.contentWindow.focus();
              printIframe.contentWindow.print();
              const cleanupResources = () => {
                setTimeout(() => {
                  if (document.body.contains(printIframe)) document.body.removeChild(printIframe);
                  URL.revokeObjectURL(pdfUrl);
                }, 100);
              };
              window.addEventListener('focus', cleanupResources, { once: true });
              setTimeout(cleanupResources, 300000);
            }, 500);
          } catch (err) {
            console.error('Error printing:', err);
            window.open(pdfUrl, '_blank');
            setTimeout(() => {
              if (document.body.contains(printIframe)) document.body.removeChild(printIframe);
              URL.revokeObjectURL(pdfUrl);
            }, 1000);
          }
        };

        printIframe.onerror = function () {
          console.error('Error loading PDF in iframe');
          window.open(pdfUrl, '_blank');
          if (document.body.contains(printIframe)) document.body.removeChild(printIframe);
          URL.revokeObjectURL(pdfUrl);
        };

        printIframe.src = pdfUrl;
      }
    } catch (error) {
      console.error('Error processing bill:', error);
      setError('An error occurred while processing the bill.');
    } finally {
      setPrintingBillNo(null);
    }
  };

  const openTimeModal = () => setShowTimeModal(true);
  const closeTimeModal = () => setShowTimeModal(false);

  const applyTimeFilter = () => {
    if (!startTimeHour) {
      setError('Please select at least a start time');
      return;
    }
    setShowTimeModal(false);
    fetchBillsWithTimeRange();
  };

  const removeTimeFilter = () => {
    setStartTimeHour('');
    setStartTimeMinute('00');
    setEndTimeHour('');
    setEndTimeMinute('00');
    setStartTimePeriod('AM');
    setEndTimePeriod('PM');
    setIsTimeFilterActive(false);
    fetchBills();
  };

  const hourOptions = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
  const minuteOptions = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));

  const getTimeDisplayString = () => {
    if (!startTimeHour) return '';
    const startStr = `${startTimeHour}:${startTimeMinute} ${startTimePeriod}`;
    const endStr = endTimeHour ? `${endTimeHour}:${endTimeMinute} ${endTimePeriod}` : 'onwards';
    return `${startStr} - ${endStr}`;
  };

  // ----- Local search filter + client-side sort + summary -----
  // Sorting by date, then bill no, happens entirely in the browser, so
  // toggling Recent/Oldest never triggers a new API call.
  const sortBills = (list) =>
    [...list].sort((a, b) => {
      const dateA = new Date(a.bill_date).getTime();
      const dateB = new Date(b.bill_date).getTime();
      if (dateA !== dateB) {
        return sortOrder === 'Recent' ? dateB - dateA : dateA - dateB;
      }
      const billNoA = parseInt(a.billNo);
      const billNoB = parseInt(b.billNo);
      return sortOrder === 'Recent' ? billNoB - billNoA : billNoA - billNoB;
    });

  const filteredBills = sortBills(
    bills.filter((b) => {
      const q = searchQuery.trim().toLowerCase();
      if (!q) return true;
      return (
        String(b.billNo ?? '').toLowerCase().includes(q) ||
        String(b.customerName ?? '').toLowerCase().includes(q) ||
        String(b.customerMobileNo ?? '').toLowerCase().includes(q)
      );
    })
  );

  const totalAmountSum = filteredBills.reduce((sum, b) => sum + (Number(b.final_amount) || 0), 0);

  // Inline style for the Cancel button in the loading overlay (no CSS-module change needed).
  const cancelFetchBtnStyle = {
    marginTop: '4px',
    padding: '8px 20px',
    border: 'none',
    borderRadius: '8px',
    background: '#dc2626',
    color: '#fff',
    fontWeight: 600,
    fontSize: '13px',
    cursor: 'pointer',
  };

  return (
    <div className={styles.billViewer}>
      <h1 className={styles.heading}>Intercompany Bill Viewer</h1>

      {error && <p className={styles.error}>{error}</p>}

      {/* -------------------- Filter row (buttons + date chip) -------------------- */}
      <div className={styles.filterRow}>
        <div className={styles.filters}>
          {['Today', 'This Week', 'This Month', 'Previous Month', 'This FY'].map((filter) => (
            <button
              key={filter}
              className={`${styles.filterButton} ${activeFilter === filter ? styles.active : ''}`}
              onClick={() => handleDateFilter(filter)}
              disabled={isLoading}
            >
              {filter}
            </button>
          ))}
          <button
            className={`${styles.filterButton} ${activeFilter === 'Custom Date' ? styles.active : ''}`}
            onClick={openCustomDateModal}
            disabled={isLoading}
          >
            Custom Date
          </button>
          <button
            className={`${styles.filterButton} ${styles.timeRangeButton} ${isTimeFilterActive ? styles.active : ''}`}
            onClick={openTimeModal}
            disabled={isLoading}
          >
            Time Range
          </button>
        </div>

        {startDate && endDate && (
          <div className={styles.dateChip}>
            <span>{format(new Date(startDate), 'dd MMM yyyy')}</span>
            <span className={styles.arrow}>→</span>
            <span>{format(new Date(endDate), 'dd MMM yyyy')}</span>
            {isTimeFilterActive && (
              <>
                <span className={styles.timeChip}>{getTimeDisplayString()}</span>
                <button className={styles.removeTimeFilter} onClick={removeTimeFilter} disabled={isLoading}>
                  ×
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* -------------------- Toolbar -------------------- */}
      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <span className={styles.searchIcon}>🔍</span>
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Search by bill no, customer name or mobile"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className={styles.clearSearch} onClick={() => setSearchQuery('')}>
              ×
            </button>
          )}
        </div>

        <div className={styles.sortOrder}>
          <span className={styles.sortLabel}>Sort By:</span>
          <div className={styles.radioGroup}>
            <label>
              <input
                type="radio"
                checked={sortOrder === 'Recent'}
                onChange={() => handleSortOrderChange('Recent')}
                disabled={isLoading}
              />
              Recent
            </label>
            <label>
              <input
                type="radio"
                checked={sortOrder === 'Oldest'}
                onChange={() => handleSortOrderChange('Oldest')}
                disabled={isLoading}
              />
              Oldest
            </label>
          </div>
        </div>

        {quarters && (
          <div className={styles.quarterDropdown}>
            <select value={selectedQuarter} onChange={(e) => handleQuarterChange(e.target.value)} disabled={isLoading}>
              <option value="">Select Quarter</option>
              {Object.entries(quarterGroups).map(([group, entries]) => (
                <optgroup key={group} label={group}>
                  {entries.map(([key, q]) => (
                    <option key={key} value={key}>
                      {q.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* -------------------- Custom Date Modal -------------------- */}
      {showCustomDateModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h2>Select Date Range</h2>
            <button className={styles.modalClose} onClick={() => setShowCustomDateModal(false)}>
              ×
            </button>
            <div className={styles.dateInputs}>
              <label>
                Start Date
                <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
              </label>
              <label>
                End Date
                <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
              </label>
            </div>
            <div className={styles.modalActions}>
              <button className={styles.findButton} onClick={applyCustomDate}>
                Find
              </button>
              <button className={styles.cancelButton} onClick={() => setShowCustomDateModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* -------------------- Time Range Modal -------------------- */}
      {showTimeModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h2>Select Time Range</h2>
            <button className={styles.modalClose} onClick={closeTimeModal}>
              ×
            </button>
            <div className={styles.timeInputsContainer}>
              <div className={styles.timeInputGroup}>
                <label>Start Time *</label>
                <div className={styles.timeInputWrapper}>
                  <select value={startTimeHour} onChange={(e) => setStartTimeHour(e.target.value)} className={styles.timeSelect}>
                    <option value="">HH</option>
                    {hourOptions.map((hour) => (
                      <option key={hour} value={hour}>
                        {hour}
                      </option>
                    ))}
                  </select>
                  <span className={styles.timeSeparator}>:</span>
                  <select value={startTimeMinute} onChange={(e) => setStartTimeMinute(e.target.value)} className={styles.timeSelect}>
                    {minuteOptions.map((minute) => (
                      <option key={minute} value={minute}>
                        {minute}
                      </option>
                    ))}
                  </select>
                  <select value={startTimePeriod} onChange={(e) => setStartTimePeriod(e.target.value)} className={styles.periodSelect}>
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>

              <div className={styles.timeInputGroup}>
                <label>End Time (Optional)</label>
                <div className={styles.timeInputWrapper}>
                  <select value={endTimeHour} onChange={(e) => setEndTimeHour(e.target.value)} className={styles.timeSelect}>
                    <option value="">HH</option>
                    {hourOptions.map((hour) => (
                      <option key={hour} value={hour}>
                        {hour}
                      </option>
                    ))}
                  </select>
                  <span className={styles.timeSeparator}>:</span>
                  <select value={endTimeMinute} onChange={(e) => setEndTimeMinute(e.target.value)} className={styles.timeSelect}>
                    {minuteOptions.map((minute) => (
                      <option key={minute} value={minute}>
                        {minute}
                      </option>
                    ))}
                  </select>
                  <select value={endTimePeriod} onChange={(e) => setEndTimePeriod(e.target.value)} className={styles.periodSelect}>
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>
            </div>
            <div className={styles.modalActions}>
              <button className={styles.applyTimeButton} onClick={applyTimeFilter} disabled={isLoading}>
                Apply Time Filter
              </button>
              <button className={styles.cancelTimeButton} onClick={closeTimeModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* -------------------- Table (only scroll region) -------------------- */}
      <div className={styles.tableWrap}>
        {isLoading && (
          <div className={styles.loadingOverlay}>
            <div className={styles.spinner} />
            <span className={styles.loadingText}>Loading bills…</span>
            <button type="button" style={cancelFetchBtnStyle} onClick={cancelFetch}>
              Cancel
            </button>
          </div>
        )}

        {filteredBills.length > 0 ? (
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Bill No</th>
                  <th>Date</th>
                  <th>Time</th>
                  <th className={styles.wrapCell}>Customer Name</th>
                  <th>Customer Mobile</th>
                  <th>Bill Type</th>
                  <th className={styles.wrapCell}>School</th>
                  <th>Amount</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredBills.map((bill) => {
                  const amount = Number(bill.final_amount) || 0;
                  const isPrinting = printingBillNo === bill.billNo;
                  return (
                    <tr key={bill.billNo}>
                      <td>{bill.billNo}</td>
                      <td>{format(new Date(bill.bill_date), 'dd MMM yyyy')}</td>
                      <td>
                        {bill.time
                          ? (() => {
                              const [h, m] = bill.time.split(':');
                              const hour = ((+h + 11) % 12) + 1;
                              const ampm = +h >= 12 ? 'PM' : 'AM';
                              return `${hour.toString().padStart(2, '0')}:${m} ${ampm}`;
                            })()
                          : '-'}
                      </td>
                      <td className={styles.wrapCell}>{bill.customerName}</td>
                      <td>{bill.customerMobileNo}</td>
                      <td>{bill.billType}</td>
                      <td className={styles.wrapCell}>{bill.schoolName}</td>
                      <td className={amount < 0 ? styles.negativeAmount : ''}>{bill.final_amount}</td>
                      <td className={styles.actionButtons}>
                        <button
                          className={styles.actionBtn}
                          onClick={() => handleViewDetails(bill)}
                          disabled={isLoading || isPrinting}
                        >
                          View
                        </button>
                        <button
                          className={styles.actionBtn}
                          onClick={() => handlePrintBill(bill.billNo)}
                          disabled={isLoading || isPrinting}
                        >
                          {isPrinting ? 'Printing…' : 'Print'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          !error && !isLoading && <p className={styles.empty}>No bills to display.</p>
        )}
      </div>

      {/* -------------------- Summary -------------------- */}
      {filteredBills.length > 0 && (
        <div className={styles.summaryBar}>
          <div className={styles.summaryItem}>
            <span>Total Bills</span>
            <strong>{filteredBills.length}</strong>
          </div>
          <div className={`${styles.summaryItem} ${styles.summaryTotal}`}>
            <span>Total Amount</span>
            <strong>₹{totalAmountSum.toFixed(2)}</strong>
          </div>
        </div>
      )}

      {/* -------------------- Bill detail popup -------------------- */}
      {selectedBill && (
        <div className={styles.popup}>
          <div className={styles.popupContent}>
            <button className={styles.closePopup} onClick={closePopup}>
              Close
            </button>
            <h2>Bill Details</h2>
            <div className={styles.details}>
              <p>
                <strong>Bill No:</strong> {selectedBill.billNo}
              </p>
              <p>
                <strong>Date:</strong> {selectedBill.bill_date}
              </p>
              <p>
                <strong>Customer Mobile:</strong> {selectedBill.customerMobileNo}
              </p>
            </div>
            <h3>Items</h3>
            <table className={styles.popupTable}>
              <thead>
                <tr>
                  <th>Item Type</th>
                  <th>School</th>
                  <th>Color</th>
                  <th>Size</th>
                  <th>Quantity</th>
                  <th>Price</th>
                  <th>Discount %</th>
                  <th>Sell Price</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {selectedBill.bill.map((item, index) => (
                  <tr key={index}>
                    <td>{item.itemType}</td>
                    <td>{item.itemCategory}</td>
                    <td>{item.itemColor}</td>
                    <td>{item.itemSize}</td>
                    <td>{item.quantity}</td>
                    <td>{item.price}</td>
                    <td>{item.discount}</td>
                    <td>{item.sellPrice}</td>
                    <td>{item.total_amount}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="8" style={{ textAlign: 'right' }}>
                    <strong>Total Amount:</strong>
                  </td>
                  <td>
                    <strong>{selectedBill.final_amount}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      <PrintBillModal isOpen={showPrintModal} onClose={() => setShowPrintModal(false)} billInfo={billInfo} />
    </div>
  );
};

export default IntercompanyBillViewer;